import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { XMarkIcon, TrashIcon, MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import ingestApi from "../../../lib/ingestApi";
import SessionReviewModal from "./SessionReviewModal";

const MAX_FILE_BYTES = 30 * 1024 * 1024;
const ACCEPTED_TYPES = /application\/pdf|image\//;

// OCR 처리가 끝나지 않은 세션 상태 — 이 동안엔 검수 모달 진입 차단
const OCR_PENDING_STATUSES = new Set(["queued", "processing", "retrying"]);

const getDateOffset = (days) => {
	const date = new Date();
	date.setDate(date.getDate() + days);
	return date.toISOString().split("T")[0];
};

const formatDateTime = (value) => {
	if (!value) return "-";
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return String(value);
	return date.toLocaleString();
};

const formatBytes = (bytes) => {
	if (!bytes && bytes !== 0) return "-";
	if (bytes < 1024) return `${bytes} B`;
	if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
	return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatStatus = (status) => {
	const labels = {
		queued: "대기",
		uploading: "업로드 중",
		uploaded: "업로드 완료",
		registered: "등록 요청됨",
		failed: "실패",
		rejected: "제외",
		processing: "처리 중",
		staged: "OCR 완료",
		staged_with_errors: "OCR 오류 있음",
		committing: "등록 중",
		committed: "등록 완료",
		partial_committed: "부분 등록",
		commit_failed: "등록 실패",
	};
	return labels[status] || status || "-";
};

const buildFileEntry = (file) => ({
	id: typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`,
	file,
	name: file.name,
	size: file.size,
	type: file.type || "application/octet-stream",
	status: "queued",
	progress: 0,
	s3Key: null,
	uploadedAt: null,
	error: null,
});

export default function TicketRegister() {
	const [activeTab, setActiveTab] = useState("special");
	const [specialFiles, setSpecialFiles] = useState([]);
	const [normalFiles, setNormalFiles] = useState([]);
	const [isLoading, setIsLoading] = useState(false);
	const [workerHealth, setWorkerHealth] = useState(null); // null = checking, true = alive, false = down

	const [error, setError] = useState(null);
	const [sessionStatus, setSessionStatus] = useState(null);
	const [activeSessionId, setActiveSessionId] = useState(null);
	const [sessionList, setSessionList] = useState([]);
	const [sessionTotal, setSessionTotal] = useState(0);
	const [sessionLoading, setSessionLoading] = useState(false);
	const [sessionFilters, setSessionFilters] = useState(() => ({
		startDate: getDateOffset(-7),
		endDate: getDateOffset(0),
		status: "",
	}));
	const [reviewSessionId, setReviewSessionId] = useState(null);
	const [deleteLoading, setDeleteLoading] = useState(null);
	// "special" | "normal" | null — 현재 drag over 중인 업로드 영역 식별
	const [dragOverArea, setDragOverArea] = useState(null);

	const fileInputRef = useRef(null);
	const normalFileInputRef = useRef(null);
	const wsRef = useRef(null);

	const updateFile = (mode, fileId, patch) => {
		const setter = mode === "special" ? setSpecialFiles : setNormalFiles;
		setter((prev) => prev.map((file) => (file.id === fileId ? { ...file, ...patch } : file)));
	};

	const addEntries = (mode, entries) => {
		const setter = mode === "special" ? setSpecialFiles : setNormalFiles;
		setter((prev) => [...prev, ...entries]);
	};

	const listForMode = (mode) => (mode === "special" ? specialFiles : normalFiles);

	// uploaded 신규 파일이 있고, 진행 중(uploading)이 없을 때만 등록 가능
	const pendingSpecialCount = useMemo(
		() => specialFiles.filter((item) => item.status === "uploaded").length,
		[specialFiles]
	);
	const pendingNormalCount = useMemo(
		() => normalFiles.filter((item) => item.status === "uploaded").length,
		[normalFiles]
	);
	const canSubmitSpecial = useMemo(
		() => pendingSpecialCount > 0 && !specialFiles.some((i) => i.status === "uploading"),
		[pendingSpecialCount, specialFiles]
	);
	const canSubmitNormal = useMemo(
		() => pendingNormalCount > 0 && !normalFiles.some((i) => i.status === "uploading"),
		[pendingNormalCount, normalFiles]
	);

	const fetchSessionStatus = async (sessionId) => {
		try {
			const response = await ingestApi.getSession(sessionId);
			setSessionStatus(response.data);
			setSessionList((prev) =>
				prev.map((item) =>
					item.id === response.data.id
						? {
								...item,
								status: response.data.status,
								total_pages: response.data.total_pages,
								processed_pages: response.data.processed_pages,
								committed_pages: response.data.committed_pages,
								error_count: response.data.error_count,
						  }
						: item
				)
			);
			return response.data;
		} catch (err) {
			setError("세션 상태를 불러오는 중 오류가 발생했습니다.");
			return null;
		}
	};

	useEffect(() => {
		const checkHealth = async () => {
			try {
				const res = await ingestApi.checkHealth();
				setWorkerHealth(res.data?.workers_alive ?? false);
			} catch {
				setWorkerHealth(false);
			}
		};
		checkHealth();
		const interval = setInterval(checkHealth, 30000);
		return () => clearInterval(interval);
	}, []);

	useEffect(() => {
		const savedSession = localStorage.getItem("ingest-active-session");
		if (!savedSession) return;
		const parsed = Number(savedSession);
		if (!Number.isNaN(parsed)) {
			setActiveSessionId(parsed);
		}
	}, []);

	useEffect(() => {
		if (activeSessionId) {
			localStorage.setItem("ingest-active-session", String(activeSessionId));
		} else {
			localStorage.removeItem("ingest-active-session");
		}
	}, [activeSessionId]);

	useEffect(() => {
		if (!activeSessionId) return;

		let cancelled = false;
		let wsAlive = false;
		let pollingInterval = null;

		const TERMINAL_STATUSES = new Set([
			"staged",
			"staged_with_errors",
			"committed",
			"partial_committed",
			"commit_failed",
			"failed",
		]);

		const refresh = async () => {
			if (cancelled) return;
			const status = await fetchSessionStatus(activeSessionId);
			// terminal 상태면 폴링 중단, WS도 닫기
			if (status && TERMINAL_STATUSES.has(status.status)) {
				if (pollingInterval) {
					clearInterval(pollingInterval);
					pollingInterval = null;
				}
				if (wsRef.current) {
					wsRef.current.close();
				}
			}
		};

		refresh();

		if (wsRef.current) {
			wsRef.current.close();
		}

		// WebSocket 연결 시도. 실패하거나 메시지가 끊겨도 폴링으로 fallback.
		let ws = null;
		try {
			ws = new WebSocket(ingestApi.getWebSocketUrl(activeSessionId));
			wsRef.current = ws;
			ws.onopen = () => {
				wsAlive = true;
			};
			ws.onmessage = () => {
				refresh();
			};
			ws.onclose = () => {
				wsAlive = false;
				// 동일 인스턴스일 때만 ref 정리 (경쟁 상황 방지)
				if (wsRef.current === ws) wsRef.current = null;
			};
			ws.onerror = () => {
				wsAlive = false;
				if (wsRef.current === ws) wsRef.current = null;
			};
		} catch (_err) {
			if (wsRef.current === ws) wsRef.current = null;
		}

		// 폴링 fallback — WebSocket이 살아있지 않을 때만 호출하여 중복 요청 최소화.
		pollingInterval = setInterval(() => {
			if (!wsAlive) refresh();
		}, 3000);

		return () => {
			cancelled = true;
			if (ws) ws.close();
			if (pollingInterval) clearInterval(pollingInterval);
		};
	}, [activeSessionId]);


	const loadSessions = async (filters = sessionFilters) => {
		setSessionLoading(true);
		try {
			const params = {
				start_date: filters.startDate || undefined,
				end_date: filters.endDate || undefined,
				status: filters.status || undefined,
				limit: 50,
				offset: 0,
			};
			const response = await ingestApi.listSessions(params);
			setSessionList(response.data?.items || []);
			setSessionTotal(response.data?.total || 0);
		} catch (err) {
			setSessionList([]);
			setSessionTotal(0);
			setError("세션 목록을 불러오는 중 오류가 발생했습니다.");
		} finally {
			setSessionLoading(false);
		}
	};

	const handleSessionSearch = () => {
		loadSessions();
	};

	const handleDeleteSession = async (sessionId) => {
		if (!window.confirm("이 세션을 삭제하시겠습니까? 모든 파일과 페이지가 삭제됩니다.")) return;
		setDeleteLoading(sessionId);
		try {
			await ingestApi.deleteSession(sessionId);
			setSessionList((prev) => prev.filter((s) => s.id !== sessionId));
			setSessionTotal((prev) => Math.max(0, prev - 1));
			if (activeSessionId === sessionId) {
				setActiveSessionId(null);
				setSessionStatus(null);
			}
		} catch (err) {
			setError("세션 삭제에 실패했습니다.");
		} finally {
			setDeleteLoading(null);
		}
	};

	const handleReviewModalClose = () => {
		setReviewSessionId(null);
		loadSessions();
		if (activeSessionId) {
			fetchSessionStatus(activeSessionId);
		}
	};

	const handleReviewModalCommit = () => {
		loadSessions();
		if (activeSessionId) {
			fetchSessionStatus(activeSessionId);
		}
	};

	useEffect(() => {
		if (activeTab !== "progress") return;
		loadSessions();
	}, [activeTab]);

	const handleFiles = async (files, mode) => {
		setError(null);
		const fileArray = Array.from(files);
		const validEntries = [];
		const invalidEntries = [];

		fileArray.forEach((file) => {
			if (!ACCEPTED_TYPES.test(file.type) && !file.name.toLowerCase().endsWith(".pdf")) {
				const entry = buildFileEntry(file);
				entry.status = "rejected";
				entry.error = "지원하지 않는 파일 형식";
				invalidEntries.push(entry);
				return;
			}
			if (file.size > MAX_FILE_BYTES) {
				const entry = buildFileEntry(file);
				entry.status = "rejected";
				entry.error = "30MB 이하 파일만 지원";
				invalidEntries.push(entry);
				return;
			}
			validEntries.push(buildFileEntry(file));
		});

		if (invalidEntries.length) {
			addEntries(mode, invalidEntries);
		}

		if (!validEntries.length) return;

		addEntries(mode, validEntries);
		setIsLoading(true);

		try {
			const presignPayload = validEntries.map((entry) => ({
				filename: entry.name,
				content_type: entry.type || "application/octet-stream",
				size_bytes: entry.size,
			}));

			const response = await ingestApi.presign(presignPayload);
			const presigned = response.data?.files || [];

			const uploadTasks = presigned.map((item, index) => {
				const entry = validEntries[index];
				if (!entry) return Promise.resolve();

				updateFile(mode, entry.id, {
					status: "uploading",
					s3Key: item.s3_key,
					progress: 0,
				});

				return axios
					.put(item.upload_url, entry.file, {
						headers: {
							"Content-Type": entry.type || "application/octet-stream",
						},
						onUploadProgress: (evt) => {
							if (!evt.total) return;
							const percent = Math.round((evt.loaded / evt.total) * 100);
							updateFile(mode, entry.id, { progress: percent });
						},
					})
					.then(() => {
						updateFile(mode, entry.id, {
							status: "uploaded",
							progress: 100,
							uploadedAt: new Date().toISOString(),
						});
					})
					.catch((err) => {
						updateFile(mode, entry.id, {
							status: "failed",
							error: err.message || "업로드 실패",
						});
					});
			});

			await Promise.allSettled(uploadTasks);
		} catch (err) {
			validEntries.forEach((entry) => {
				updateFile(mode, entry.id, {
					status: "failed",
					error: "Presign 요청 실패",
				});
			});
			setError("S3 업로드 준비에 실패했습니다.");
		} finally {
			setIsLoading(false);
		}
	};

	const handleUploadClick = (mode) => {
		if (mode === "special") {
			fileInputRef.current?.click();
		} else {
			normalFileInputRef.current?.click();
		}
	};

	const handleFileChange = (e, mode) => {
		if (e.target.files && e.target.files.length > 0) {
			handleFiles(e.target.files, mode);
		}
	};

	const handleCreateSession = async (mode) => {
		const files = listForMode(mode).filter((item) => item.status === "uploaded");
		if (!files.length) {
			setError("업로드 완료된 파일이 없습니다.");
			return;
		}

		const submittedIds = new Set(files.map((f) => f.id));
		setIsLoading(true);
		try {
			const payload = files.map((item) => ({
				s3_key: item.s3Key,
				filename: item.name,
				content_type: item.type,
				size_bytes: item.size,
			}));
			const response = await ingestApi.createSession(payload);
			const sessionId = response.data?.session_id;
			// 등록 요청된 파일들을 'registered'로 마킹하고 sessionId 기록
			const setter = mode === "special" ? setSpecialFiles : setNormalFiles;
			setter((prev) =>
				prev.map((item) =>
					submittedIds.has(item.id)
						? { ...item, status: "registered", sessionId }
						: item
				)
			);
			setActiveSessionId(sessionId);
			setActiveTab("progress");
			await fetchSessionStatus(sessionId);
			await loadSessions();
		} catch (err) {
			setError("세션 생성에 실패했습니다.");
		} finally {
			setIsLoading(false);
		}
	};

	const renderUploadTable = (files) => (
		<div className="overflow-x-auto">
			<table className="min-w-full divide-y divide-gray-200">
				<thead className="bg-gray-50">
					<tr>
						<th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">번호</th>
						<th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">파일명</th>
						<th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">상태</th>
						<th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">업로드</th>
						<th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">용량</th>
					</tr>
				</thead>
				<tbody className="bg-white divide-y divide-gray-200">
					{files.length > 0 ? (
						files.map((file, index) => (
							<tr
								key={file.id}
								className={`hover:bg-gray-50 ${file.status === "registered" ? "opacity-60" : ""}`}
							>
								<td className="px-4 py-3 text-sm text-gray-900">{index + 1}</td>
								<td className="px-4 py-3 text-sm text-gray-900">{file.name}</td>
								<td className="px-4 py-3 text-sm text-gray-900">
									<span
										className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
											file.status === "uploaded"
												? "bg-green-100 text-green-700"
												: file.status === "uploading"
												? "bg-blue-100 text-blue-700"
												: file.status === "failed" || file.status === "rejected"
												? "bg-red-100 text-red-700"
												: "bg-gray-100 text-gray-600"
										}`}
									>
										{formatStatus(file.status)}
									</span>
									{file.error && (
										<span className="block text-xs text-red-500 mt-1">{file.error}</span>
									)}
								</td>
								<td className="px-4 py-3 text-sm text-gray-900">
									<div className="w-full bg-gray-200 rounded-full h-2">
										<div
											className="bg-blue-600 h-2 rounded-full"
											style={{ width: `${file.progress || 0}%` }}
										></div>
									</div>
									<span className="text-xs text-gray-500">{file.progress || 0}%</span>
								</td>
								<td className="px-4 py-3 text-sm text-gray-900">{formatBytes(file.size)}</td>
							</tr>
						))
					) : (
						<tr>
							<td colSpan="5" className="px-4 py-4 text-center text-sm text-gray-500">
								파일을 업로드해주세요.
							</td>
						</tr>
					)}
				</tbody>
			</table>
		</div>
	);

	const renderSessionList = () => (
		<div className="space-y-3">
			<div className="flex flex-wrap items-end justify-between gap-4">
				<div className="flex flex-wrap gap-4">
					<div className="flex flex-col">
						<label className="text-xs text-gray-500 mb-1">시작일</label>
						<input
							type="date"
							value={sessionFilters.startDate}
							onChange={(e) =>
								setSessionFilters((prev) => ({ ...prev, startDate: e.target.value }))
							}
							className="border border-gray-300 rounded-md px-2 py-1 text-sm"
						/>
					</div>
					<div className="flex flex-col">
						<label className="text-xs text-gray-500 mb-1">종료일</label>
						<input
							type="date"
							value={sessionFilters.endDate}
							onChange={(e) =>
								setSessionFilters((prev) => ({ ...prev, endDate: e.target.value }))
							}
							className="border border-gray-300 rounded-md px-2 py-1 text-sm"
						/>
					</div>
					<div className="flex flex-col">
						<label className="text-xs text-gray-500 mb-1">상태</label>
						<select
							value={sessionFilters.status}
							onChange={(e) =>
								setSessionFilters((prev) => ({ ...prev, status: e.target.value }))
							}
							className="border border-gray-300 rounded-md px-2 py-1 text-sm"
						>
							<option value="">전체</option>
							<option value="queued">대기</option>
							<option value="processing">처리 중</option>
							<option value="staged">OCR 완료</option>
							<option value="staged_with_errors">OCR 오류 있음</option>
							<option value="committed">등록 완료</option>
							<option value="partial_committed">부분 등록</option>
							<option value="commit_failed">등록 실패</option>
						</select>
					</div>
				</div>
				<button
					className="bg-gray-900 hover:bg-gray-800 text-white px-4 py-2 rounded-md text-sm"
					onClick={handleSessionSearch}
				>
					조회
				</button>
			</div>

			<div className="text-sm text-gray-500">총 {sessionTotal}건</div>

			<div className="overflow-x-auto">
				<table className="min-w-full divide-y divide-gray-200">
					<thead className="bg-gray-50">
						<tr>
							<th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">세션</th>
							<th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">상태</th>
							<th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">파일</th>
							<th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">페이지</th>
							<th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">오류</th>
							<th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">생성일</th>
							<th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">선택</th>
						</tr>
					</thead>
					<tbody className="bg-white divide-y divide-gray-200">
						{sessionLoading ? (
							<tr>
								<td colSpan="7" className="px-4 py-4 text-center text-sm text-gray-500">
									세션을 불러오는 중...
								</td>
							</tr>
						) : sessionList.length ? (
							sessionList.map((session) => (
								<tr
									key={session.id}
									className={`hover:bg-gray-50 ${
										activeSessionId === session.id ? "bg-blue-50" : ""
									}`}
								>
									<td className="px-4 py-3 text-sm text-gray-900">#{session.id}</td>
									<td className="px-4 py-3 text-sm text-gray-900">
										{formatStatus(session.status)}
									</td>
									<td className="px-4 py-3 text-sm text-gray-900">{session.total_files}</td>
									<td className="px-4 py-3 text-sm text-gray-900">
										{session.processed_pages} / {session.total_pages}
									</td>
									<td className="px-4 py-3 text-sm text-gray-900">{session.error_count}</td>
									<td className="px-4 py-3 text-sm text-gray-900">
										{formatDateTime(session.created_at)}
									</td>
									<td className="px-4 py-3 text-sm text-gray-900">
										<div className="flex items-center gap-2">
											{(() => {
												const isOcrPending = OCR_PENDING_STATUSES.has(session.status);
												return (
													<button
														className={`text-sm flex items-center gap-1 ${
															isOcrPending
																? "text-gray-400 cursor-not-allowed"
																: "text-blue-600 hover:text-blue-800"
														}`}
														onClick={() => {
															if (isOcrPending) {
																toast.info("OCR 완료까지 잠시만 기다려주세요.");
																return;
															}
															setReviewSessionId(session.id);
														}}
														title={isOcrPending ? "OCR 처리 중입니다" : ""}
													>
														<MagnifyingGlassIcon className="h-4 w-4" />
														검수
													</button>
												);
											})()}
											<button
												className="text-red-600 hover:text-red-800 text-sm flex items-center gap-1"
												onClick={() => handleDeleteSession(session.id)}
												disabled={deleteLoading === session.id}
											>
												<TrashIcon className="h-4 w-4" />
												{deleteLoading === session.id ? "..." : "삭제"}
											</button>
										</div>
									</td>
								</tr>
							))
						) : (
							<tr>
								<td colSpan="7" className="px-4 py-4 text-center text-sm text-gray-500">
									조회된 세션이 없습니다.
								</td>
							</tr>
						)}
					</tbody>
				</table>
			</div>
		</div>
	);

	const renderProgressTab = () => {
		const progressPercent = sessionStatus?.total_pages
			? Math.round((sessionStatus.processed_pages / sessionStatus.total_pages) * 100)
			: 0;

		return (
			<div className="p-6 space-y-6">
				<div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
					<div className="text-sm font-semibold text-gray-700 mb-3">세션 목록</div>
					{renderSessionList()}
				</div>

				{activeSessionId && sessionStatus && ["processing", "queued"].includes(sessionStatus.status) && (
					<div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
						<div className="flex items-center justify-between mb-2">
							<span className="text-sm font-medium text-blue-800">
								세션 #{activeSessionId} 처리 중
							</span>
							<span className="text-sm text-blue-600">{progressPercent}%</span>
						</div>
						<div className="w-full bg-blue-200 rounded-full h-2">
							<div
								className="bg-blue-600 h-2 rounded-full transition-all"
								style={{ width: `${progressPercent}%` }}
							></div>
						</div>
						<div className="text-xs text-blue-600 mt-1">
							{sessionStatus.processed_pages || 0} / {sessionStatus.total_pages || 0} 페이지 완료
						</div>
					</div>
				)}
			</div>
		);
	};

	return (
		<div className="py-6">
			<div className="flex items-center gap-3 mb-6">
				<h1 className="text-2xl font-bold text-gray-900">승차권 등록</h1>
				<span
					className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
						workerHealth === null
							? "bg-gray-100 text-gray-500"
							: workerHealth
							? "bg-green-100 text-green-700"
							: "bg-red-100 text-red-700"
					}`}
				>
					<span
						className={`inline-block w-2 h-2 rounded-full ${
							workerHealth === null
								? "bg-gray-400"
								: workerHealth
								? "bg-green-500"
								: "bg-red-500"
						}`}
					/>
					{workerHealth === null
						? "확인 중..."
						: workerHealth
						? "OCR 처리 가능"
						: "OCR 처리 불가"}
				</span>
			</div>

			<div className="mb-4">
				<div className="flex flex-wrap">
					<button
						className={`py-2 px-4 ${
							activeTab === "special" ? "bg-blue-600 text-white" : "bg-gray-200 text-gray-700"
						} rounded-t-lg`}
						onClick={() => setActiveTab("special")}
					>
						승차권 업로드 (바우처/SRT/홈티켓)
					</button>
					<button
						className={`py-2 px-4 ml-1 ${
							activeTab === "normal" ? "bg-blue-600 text-white" : "bg-gray-200 text-gray-700"
						} rounded-t-lg`}
						onClick={() => setActiveTab("normal")}
					>
						승차권 업로드 (일반)
					</button>
					<button
						className={`py-2 px-4 ml-1 ${
							activeTab === "progress" ? "bg-blue-600 text-white" : "bg-gray-200 text-gray-700"
						} rounded-t-lg`}
						onClick={() => setActiveTab("progress")}
					>
						진행 상황
					</button>
				</div>
			</div>

			<div className="bg-white shadow rounded-lg">
				{activeTab === "special" && (
					<div className="p-4">
						<div
							onDragOver={(e) => {
								e.preventDefault();
								if (dragOverArea !== "special") setDragOverArea("special");
							}}
							onDragLeave={(e) => {
								e.preventDefault();
								setDragOverArea((cur) => (cur === "special" ? null : cur));
							}}
							onDrop={(e) => {
								e.preventDefault();
								setDragOverArea(null);
								if (e.dataTransfer.files?.length > 0) {
									handleFiles(e.dataTransfer.files, "special");
								}
							}}
							className={`border-2 border-dashed rounded-lg p-6 mb-6 text-center transition-colors ${
								dragOverArea === "special"
									? "border-blue-500 bg-blue-50"
									: "border-gray-300"
							}`}
						>
							<p className="text-gray-500 mb-2">
								바우처/SRT/홈티켓 승차권을 업로드하세요. (Drag & Drop 가능)
							</p>
							<button
								className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-md text-sm"
								onClick={() => handleUploadClick("special")}
							>
								승차권 업로드
							</button>
							<input
								type="file"
								ref={fileInputRef}
								className="hidden"
								multiple
								accept="application/pdf,image/*"
								onChange={(e) => handleFileChange(e, "special")}
							/>
						</div>

						{renderUploadTable(specialFiles)}

						<div className="mt-4 flex justify-end">
							<button
								className={`px-4 py-2 rounded-md text-sm ${
									canSubmitSpecial
										? "bg-blue-600 text-white hover:bg-blue-700"
										: "bg-gray-200 text-gray-500 cursor-not-allowed"
								}`}
								onClick={() => handleCreateSession("special")}
								disabled={!canSubmitSpecial}
							>
								{pendingSpecialCount > 0 ? `등록하기 (${pendingSpecialCount}건)` : "등록하기"}
							</button>
						</div>
					</div>
				)}

				{activeTab === "normal" && (
					<div className="p-4">
						<div
							onDragOver={(e) => {
								e.preventDefault();
								if (dragOverArea !== "normal") setDragOverArea("normal");
							}}
							onDragLeave={(e) => {
								e.preventDefault();
								setDragOverArea((cur) => (cur === "normal" ? null : cur));
							}}
							onDrop={(e) => {
								e.preventDefault();
								setDragOverArea(null);
								if (e.dataTransfer.files?.length > 0) {
									handleFiles(e.dataTransfer.files, "normal");
								}
							}}
							className={`border-2 border-dashed rounded-lg p-6 mb-6 text-center transition-colors ${
								dragOverArea === "normal"
									? "border-blue-500 bg-blue-50"
									: "border-gray-300"
							}`}
						>
							<p className="text-gray-500 mb-2">
								일반 승차권을 업로드하세요. (Drag & Drop 가능)
							</p>
							<button
								className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-md text-sm"
								onClick={() => handleUploadClick("normal")}
							>
								승차권 업로드
							</button>
							<input
								type="file"
								ref={normalFileInputRef}
								className="hidden"
								multiple
								accept="application/pdf,image/*"
								onChange={(e) => handleFileChange(e, "normal")}
							/>
						</div>

						{renderUploadTable(normalFiles)}

						<div className="mt-4 flex justify-end">
							<button
								className={`px-4 py-2 rounded-md text-sm ${
									canSubmitNormal
										? "bg-blue-600 text-white hover:bg-blue-700"
										: "bg-gray-200 text-gray-500 cursor-not-allowed"
								}`}
								onClick={() => handleCreateSession("normal")}
								disabled={!canSubmitNormal}
							>
								{pendingNormalCount > 0 ? `등록하기 (${pendingNormalCount}건)` : "등록하기"}
							</button>
						</div>
					</div>
				)}

				{activeTab === "progress" && renderProgressTab()}
			</div>

			{isLoading && (
				<div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-40">
					<div className="bg-white p-4 rounded-lg shadow-lg">
						<p className="text-gray-700">파일 처리 중...</p>
					</div>
				</div>
			)}

			{error && (
				<div className="fixed bottom-4 right-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded z-50">
					<span className="block sm:inline">{error}</span>
					<button
						className="absolute top-0 bottom-0 right-0 px-4 py-3"
						onClick={() => setError(null)}
					>
						<XMarkIcon className="h-5 w-5" />
					</button>
				</div>
			)}

			{reviewSessionId && (
				<SessionReviewModal
					sessionId={reviewSessionId}
					onClose={handleReviewModalClose}
					onCommit={handleReviewModalCommit}
				/>
			)}
		</div>
	);
}
